import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Loader2, Star, BookOpen, ChevronRight, Compass } from "lucide-react";
import api, { apiErr, fmtMoney } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtRange } from "../components/TripSwitcher";
import { toast } from "sonner";

const STATUS_PILL = {
  current: { label: "★ Current", cls: "bg-[#C9973A]/15 text-[#B87309]" },
  completed: { label: "● Completed", cls: "bg-[#1E8A5B]/10 text-[#1E8A5B]" },
  planning: { label: "◌ Planning", cls: "bg-[#2563eb]/10 text-[#2563eb]" },
  past: { label: "Past", cls: "bg-[#0d2340]/8 text-[#0d2340]/70" },
};

export default function Trips() {
  const { org } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [allTime, setAllTime] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/trips"), api.get("/trips/all-time")])
      .then(([a, b]) => { setData(a.data); setAllTime(b.data); })
      .catch((e) => toast.error(apiErr(e)));
  }, []);

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin accent-text" /></div>;
  const trips = data.trips || [];
  const live = trips.filter((t) => t.status === "current" || t.status === "completed");
  const planning = trips.filter((t) => t.status === "planning");
  const past = trips.filter((t) => t.status === "past");

  return (
    <div className="space-y-5" data-testid="trips-page">
      <div className="flex items-center gap-3">
        <button data-testid="trips-back" onClick={() => navigate("/app/more")} className="text-[var(--brand-navy)]"><ArrowLeft className="h-5 w-5" /></button>
        <div>
          <h1 className="font-heading font-extrabold text-3xl text-[var(--brand-navy)]">Journeys</h1>
          <p className="text-sm text-muted-foreground">Every trip {org?.org_name || "your organization"} has taken. Partners always see the <b>current</b> one.</p>
        </div>
      </div>

      <Shelf title="Current">
        {live.length === 0 && <Empty>No current journey — make one current below.</Empty>}
        {live.map((t) => <TripCard key={t.id} t={t} onOpen={() => navigate(`/app/trips/${t.id}`)} highlight />)}
      </Shelf>

      <Shelf title="Planning" hint="hidden from partners">
        {planning.map((t) => <TripCard key={t.id} t={t} onOpen={() => navigate(`/app/trips/${t.id}`)} />)}
        <button data-testid="trips-new-btn" onClick={() => navigate("/app/trips/new")}
          className="w-full rounded-2xl border-2 border-dashed border-[#0d2340]/15 py-5 flex flex-col items-center gap-2 text-sm text-muted-foreground hover:bg-white/60 transition-colors">
          {planning.length === 0 && <span>Nothing in the works yet</span>}
          <span className="inline-flex items-center gap-1.5 rounded-full accent-bg text-white font-semibold px-5 py-2.5"><Plus className="h-4 w-4" /> New journey</span>
        </button>
      </Shelf>

      <Shelf title="Past journeys">
        {past.length === 0 && <Empty>Completed trips move here when a new one becomes current. Everything stays browsable, read-only.</Empty>}
        {past.map((t) => <TripCard key={t.id} t={t} onOpen={() => navigate(`/app/trips/${t.id}`)} muted />)}
      </Shelf>

      {allTime && (
        <div data-testid="trips-all-time" className="rounded-3xl p-5 text-white flex items-center justify-between shadow-[0_14px_40px_rgba(13,35,64,0.25)]" style={{ background: "linear-gradient(135deg,#0d2340,#173a66)" }}>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] opacity-70">All-time · {org?.org_name}</div>
            <div className="font-heading font-extrabold text-2xl mt-1">{fmtMoney(allTime.raised_cents)} · {allTime.partners} partners</div>
            <div className="text-xs opacity-75 mt-0.5">{allTime.dispatches} dispatches · {allTime.prayers} prayers</div>
          </div>
          <div className="text-right text-xs opacity-80"><div className="font-heading font-extrabold text-3xl leading-none">{allTime.trips}</div>{allTime.trips === 1 ? "journey" : "journeys"}</div>
        </div>
      )}
    </div>
  );
}

function Shelf({ title, hint, children }) {
  return (
    <section>
      <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-2">{title}{hint && <span className="normal-case tracking-normal font-normal"> · {hint}</span>}</div>
      <div className="space-y-2">{children}</div>
    </section>
  );
}
function Empty({ children }) { return <p className="text-xs text-muted-foreground">{children}</p>; }

export function TripCard({ t, onOpen, highlight, muted }) {
  const s = t.stats || {};
  const pill = STATUS_PILL[t.status] || STATUS_PILL.past;
  return (
    <button type="button" data-testid={`trip-card-${t.id}`} onClick={onOpen}
      className={`w-full text-left bg-white rounded-2xl p-4 shadow-[0_4px_24px_rgba(44,44,62,0.04)] transition-transform active:scale-[0.99] ${highlight ? "border-2 border-[#C9973A]/50" : ""} ${muted ? "opacity-90" : ""}`}>
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-xl flex items-center justify-center text-white shrink-0" style={{ background: muted ? "#8a8a99" : "linear-gradient(135deg,#0d2340,#2b5a8f)" }}>
          {muted ? <BookOpen className="h-5 w-5" /> : <Compass className="h-5 w-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="font-heading font-bold text-lg text-[#0d2340] truncate">{t.trip_name}</div>
            <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${pill.cls}`}>{pill.label}</span>
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">{fmtRange(t.start_date, t.end_date)}{s.countries?.length ? ` · ${s.countries.slice(0, 3).join(", ")}${s.countries.length > 3 ? "…" : ""}` : ""}</div>
          <div className="grid grid-cols-4 gap-1 mt-3 text-center">
            <Stat n={s.dispatches} l="Dispatches" /><Stat n={s.prayers} l="Prayers" /><Stat n={s.givers} l="Givers" /><Stat n={fmtMoney(s.raised_cents || 0, t.currency || "USD")} l="Raised" />
          </div>
        </div>
        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-1" />
      </div>
      {t.status === "current" && <div className="mt-3 flex items-center gap-1 text-[11px] text-[#B87309] font-semibold"><Star className="h-3 w-3" /> What partners see today</div>}
    </button>
  );
}
function Stat({ n, l }) { return <div><div className="font-heading font-bold text-[#0d2340]">{n ?? 0}</div><div className="text-[10px] text-muted-foreground">{l}</div></div>; }
